/**
 * Phase 105-Agent-D — Lightweight marker clustering.
 *
 * Activates only when `markerCount >= CLUSTER_THRESHOLD` (25 by default).
 * Below the threshold, the underlying `<TaskPinLayer>` renders raw pins.
 *
 * The clustering algorithm is grid-bucketed, distance-aware Haversine
 * inside the visible viewport. Pure CSS pin design (no extra dep). On
 * click, a cluster zooms in one step + recomputes — the same behavior
 * as `leaflet.markercluster`'s spiderfy → zoom-in cascade, just without
 * the 32 kB of vendor code.
 *
 * Customer-side `LiveTrackingMap` is intentionally NOT clustered:
 * it only ever shows pickup + drop + 1 agent pin (max 3).
 */
import { useEffect, useMemo, useState } from "react";
import TaskPinLayer from "./TaskPinLayer";
import { useMap, Marker, L } from "../tracking/mapProvider";

const CLUSTER_THRESHOLD = 25;
const GRID_PX = 60;  // cluster radius — pixels at current zoom

function getPickupLatLng(o) {
  const lat = o?.pickup_location?.lat ?? o?.pickup_lat;
  const lng = o?.pickup_location?.lng ?? o?.pickup_lng;
  if (lat == null || lng == null) return null;
  const nlat = Number(lat), nlng = Number(lng);
  if (!Number.isFinite(nlat) || !Number.isFinite(nlng)) return null;
  return [nlat, nlng];
}

function clusterIcon(count) {
  // Size scales with cluster bucket count.
  const small = count < 10;
  const medium = count >= 10 && count < 50;
  const size = small ? 36 : medium ? 44 : 52;
  const ring = small ? "rgba(255, 107, 0, 0.35)" : medium ? "rgba(255, 107, 0, 0.55)" : "rgba(255, 107, 0, 0.75)";
  const core = small ? "#FF6B00" : medium ? "#ea580c" : "#c2410c";
  return L.divIcon({
    className: "agent-task-cluster",
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
    html: `
      <div data-testid="marker-cluster" style="
        position:relative;width:${size}px;height:${size}px;
        display:flex;align-items:center;justify-content:center;">
        <span style="
          position:absolute;inset:0;border-radius:50%;
          background:${ring};animation:cluster-pulse 1.6s ease-out infinite;"></span>
        <span style="
          position:relative;width:${size - 12}px;height:${size - 12}px;
          border-radius:50%;background:${core};color:#fff;
          font-weight:800;font-family:Outfit,system-ui,sans-serif;
          font-size:${small ? 13 : medium ? 15 : 17}px;
          display:flex;align-items:center;justify-content:center;
          border:2px solid #fff;
          box-shadow:0 4px 10px rgba(15,23,42,0.25);">
          ${count}
        </span>
      </div>
    `,
  });
}

/**
 * Internal hook: cluster the visible tasks into pixel-grid buckets at
 * the current map zoom. Re-runs on zoom + pan.
 */
function useClusters(tasks) {
  const map = useMap();
  const [tick, setTick] = useState(0);
  useEffect(() => {
    if (!map) return undefined;
    const re = () => setTick((t) => t + 1);
    map.on("zoomend", re);
    map.on("moveend", re);
    return () => { map.off("zoomend", re); map.off("moveend", re); };
  }, [map]);

  return useMemo(() => {
    if (!map) return { singles: tasks || [], clusters: [] };
    const singles = [];
    const buckets = new Map();
    for (const t of tasks || []) {
      const ll = getPickupLatLng(t);
      if (!ll) { continue; }
      const pt = map.latLngToContainerPoint(ll);
      const key = `${Math.floor(pt.x / GRID_PX)}|${Math.floor(pt.y / GRID_PX)}`;
      if (!buckets.has(key)) buckets.set(key, []);
      buckets.get(key).push({ task: t, latlng: ll, pt });
    }
    const clusters = [];
    for (const items of buckets.values()) {
      if (items.length <= 1) {
        if (items[0]) singles.push(items[0].task);
        continue;
      }
      const latAvg = items.reduce((s, it) => s + it.latlng[0], 0) / items.length;
      const lngAvg = items.reduce((s, it) => s + it.latlng[1], 0) / items.length;
      clusters.push({
        id: `c-${items.map((i) => i.task.tracking_id).join("-").slice(0, 32)}`,
        count: items.length,
        center: [latAvg, lngAvg],
        members: items.map((i) => i.task),
      });
    }
    return { singles, clusters };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tasks, tick, map]);
}

export default function TaskPinLayerWithClustering({ tasks, onSelect, selectedTrackingId }) {
  const map = useMap();
  const total = (tasks || []).length;

  // Below threshold → no clustering. Forward unchanged to TaskPinLayer.
  if (total < CLUSTER_THRESHOLD) {
    return (
      <TaskPinLayer
        tasks={tasks}
        onSelect={onSelect}
        selectedTrackingId={selectedTrackingId}
      />
    );
  }
  return (
    <ClusterImpl
      tasks={tasks}
      onSelect={onSelect}
      selectedTrackingId={selectedTrackingId}
      map={map}
    />
  );
}

function ClusterImpl({ tasks, onSelect, selectedTrackingId, map }) {
  const { singles, clusters } = useClusters(tasks);

  return (
    <>
      <TaskPinLayer
        tasks={singles}
        onSelect={onSelect}
        selectedTrackingId={selectedTrackingId}
      />
      {clusters.map((c) => (
        <Marker
          key={c.id}
          position={c.center}
          icon={clusterIcon(c.count)}
          eventHandlers={{
            click: () => {
              // Zoom-in cascade — one level deeper. The next render
              // recomputes buckets at the new zoom and naturally
              // explodes the cluster.
              const z = Math.min(map.getZoom() + 2, map.getMaxZoom());
              map.setView(c.center, z, { animate: true });
            },
          }}
        />
      ))}
    </>
  );
}
